import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { disconnectSocket } from '../hooks/useSocket';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const { data } = await api.get('/auth/me');
      setUser(data.user);
    } catch {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (identifier, password) => {
    const { data } = await api.post('/auth/login', { identifier, password });
    localStorage.setItem('token', data.token);
    // Store refresh_token for Capacitor/native (web uses httpOnly cookie set by server)
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
    api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    // Fetch full user via /auth/me so roles[] and permissions{} are populated immediately
    const { data: meData } = await api.get('/auth/me');
    setUser(meData.user);
    return meData.user;
  };

  const logout = async () => {
    try {
      // Server increments token_version and clears the httpOnly cookie
      await api.post('/auth/logout').catch(() => {});
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      delete api.defaults.headers.common['Authorization'];
      disconnectSocket();
      setUser(null);
    }
  };

  const updateUser = (patch) => setUser(prev => prev ? { ...prev, ...patch } : prev);

  // Derive from roles[] array (multi-role), falling back to single role string
  const userRoles    = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);
  const isAdmin      = userRoles.includes('admin');
  const isManager    = userRoles.includes('manager') || isAdmin;
  // Matches backend PRIVILEGED_ROLES: admin, manager, director, ceo
  const isPrivileged = userRoles.some(r => ['admin', 'manager', 'director', 'ceo'].includes(r));
  const isScience    = userRoles.some(r => ['scientist', 'principal_scientist', 'rnd_head'].includes(r)) || isAdmin;

  // Helper: can user read/write a specific page?
  // If the user has any custom page grants (whitelist mode), only listed pages are accessible.
  // Empty permissions means no custom mode — fall back to role-based access.
  const canRead  = (page) => {
    if (isPrivileged) return true;
    const perms = user?.permissions;
    if (perms && Object.keys(perms).length > 0) {
      const perm = perms[page];
      if (!perm) return false;
      if (perm.expires_at && new Date(perm.expires_at) < new Date()) return false;
      return true;
    }
    return true; // no custom mode → role-based fallback
  };
  const canWrite = (page) => {
    if (isPrivileged) return true;
    const perms = user?.permissions;
    if (perms && Object.keys(perms).length > 0) {
      const perm = perms[page];
      if (!perm) return false;
      if (perm.expires_at && new Date(perm.expires_at) < new Date()) return false;
      return perm.access_level !== 'read_only';
    }
    return true;
  };
  const getVisibility = (page) => {
    if (isPrivileged) return 'all';
    return user?.permissions?.[page]?.visibility || 'own';
  };

  // Check role-module permission.
  // In custom mode (any explicit page grants), only listed modules are accessible.
  // Without custom grants, falls back to role-module permissions.
  const canAccessModule = (module, operation = 'can_list') => {
    if (isAdmin) return true;

    const perms = user?.permissions;
    if (perms && Object.keys(perms).length > 0) {
      const pagePerm = perms[module];
      if (!pagePerm) return false;
      const expired = pagePerm.expires_at && new Date(pagePerm.expires_at) < new Date();
      if (expired) return false;
      if (operation === 'can_list' || operation === 'can_read') return true;
      return pagePerm.access_level !== 'read_only';
    }

    const rmp = user?.roleModulePermissions;
    if (!rmp || Object.keys(rmp).length === 0) return true; // migration safety
    const mod = rmp[module];
    if (!mod) return false;
    return !!mod[operation];
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, updateUser,
      userRoles, isAdmin, isManager, isPrivileged, isScience,
      canRead, canWrite, getVisibility, canAccessModule,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
